import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { fetchRemixData } from '@genfun/core/api/API';
import { useLockBodyScroll } from '../utils/useLockBodyScroll';
import { downloadFile } from '../utils/download';
import { Icon } from './Icon';
import './Lightbox.css';

const keyOf = (i) => i.media_id || i.url;

export function Lightbox({ item, items = [], token, onClose, onChange }) {
    useLockBodyScroll(true);
    const [fullUrl, setFullUrl] = useState(item.url);

    const idx = Math.max(0, items.findIndex((i) => keyOf(i) === keyOf(item)));
    const total = items.length;
    const step = (d) => { if (total > 1) onChange?.(items[(idx + d + total) % total]); };

    // Gallery photos are thumbnails; swap in the full-resolution file once it is known.
    useEffect(() => {
        setFullUrl(item.url);
        if (item.is_video || !item.media_id || !token) return undefined;
        let cancelled = false;
        fetchRemixData(token, item.media_id).then((data) => {
            if (cancelled || !data?.url) return;
            const img = new Image();
            img.onload = () => { if (!cancelled) setFullUrl(data.url); };
            img.src = data.url;
        }).catch(() => {});
        return () => { cancelled = true; };
    }, [item.url, item.media_id, item.is_video, token]);

    useEffect(() => {
        const onKey = (e) => {
            if (e.key === 'Escape') onClose();
            else if (e.key === 'ArrowRight') step(1);
            else if (e.key === 'ArrowLeft') step(-1);
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }); // re-bound every render so `step` sees the current item

    const created = item.created || item.created_at;
    const dateText = created
        ? new Date(created).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
        : 'Recently';

    return createPortal(
        <div className="lightbox" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
            <button type="button" className="iconBtn lightboxClose" onClick={onClose} aria-label="Close"><Icon name="close" /></button>
            {total > 1 && (
                <>
                    <button type="button" className="iconBtn iconBtn--lg lightboxNav lightboxNav--prev" onClick={() => step(-1)} aria-label="Previous"><Icon name="chevLeft" size={22} /></button>
                    <button type="button" className="iconBtn iconBtn--lg lightboxNav lightboxNav--next" onClick={() => step(1)} aria-label="Next"><Icon name="chevRight" size={22} /></button>
                </>
            )}
            <div className="lightboxContent">
                {item.is_video
                    ? <video key={item.url} className="lightboxMedia" src={item.url} autoPlay loop muted playsInline controls />
                    : <img className="lightboxMedia" src={fullUrl} alt="" />}
                <div className="lightboxBar glass">
                    <span className="lightboxMeta">
                        {total > 1 && <b>{idx + 1} / {total}</b>}
                        <span>{dateText}</span>
                    </span>
                    <span className="lightboxActions">
                        {item.media_id && (
                            <Link className="btn btn--ghost btn--sm" to={`/report?media_id=${encodeURIComponent(item.media_id)}`} onClick={onClose}>
                                <Icon name="flag" size={14} /> Report
                            </Link>
                        )}
                        <button type="button" className="btn btn--primary btn--sm" onClick={() => downloadFile(fullUrl)}>
                            <Icon name="download" size={15} /> Download
                        </button>
                    </span>
                </div>
            </div>
        </div>,
        document.body
    );
}
